/**
 * FormGooglePlacesAutocompleteField
 * 
 * Campo de texto con autocompletado de Google Places para direcciones argentinas.
 * Permite seleccionar direcciones con sugerencias de Google y extrae automáticamente
 * calle, localidad, provincia y código postal.
 * 
 * DEPENDENCIAS PARA REUTILIZAR:
 * - npm: @react-google-maps/api @mui/material mobx mobx-react
 * 
 * ADAPTACIONES NECESARIAS:
 * 1. FormFieldBase: Componente wrapper para campos (puede ser un simple <div>)
 * 2. El field/form debe tener:
 *    - value: valor del campo
 *    - updateValue(value): actualiza el valor
 *    - isOptional, hasValue, isValid: propiedades de validación
 *    - form.updateFieldWithValue(fieldName, value): para notificar cambios al backend
 * 
 * USO:
 *   <FormGooglePlacesAutocompleteField
 *       label="Domicilio"
 *       form={miForm}
 *       fieldName="address"
 *       onPlaceSelected={(placeData) => {
 *           // placeData contiene: address, locality, jurisdiction, postalCode, location
 *       }}
 *       isGoogleMapsLoaded={true}
 *   />
 */
import React, { useRef } from 'react';
import { TextField, useTheme } from '@mui/material';
import { observer } from 'mobx-react';
import { Autocomplete } from '@react-google-maps/api';
import FormFieldBase from './FormFieldBase';

function FormGooglePlacesAutocompleteField({
  label = undefined,
  placeholder = undefined,
  field,
  width = '100%',
  disabled = false,
  fieldName = '',
  form = undefined,
  readOnly = false,
  onPlaceSelected = undefined,
  isGoogleMapsLoaded = false,
}) {
  const theme = useTheme();
  const style = styles(theme, width);
  const googlePlacesAutocompleteRef = useRef(null);

  const formField = form ? form.getFieldByName(fieldName) : field;
  const value = formField?.value || '';
  const readonly = form ? form.isReadonly(fieldName) : readOnly;

  const onLoad = (googlePlacesAutocomplete) => {
    googlePlacesAutocompleteRef.current = googlePlacesAutocomplete;
  };

  const extractAddressComponent = (addressComponents, types) => {
    const component = addressComponents.find(component => 
      types.some(type => component.types.includes(type))
    );
    return component?.long_name || '';
  };

  const extractFirstAddressPart = (formattedAddress) => {
    const firstPart = formattedAddress.split(',')[0]?.trim();
    return firstPart || formattedAddress;
  };

  const buildStreetAddress = (route, streetNumber, fallbackAddress) => {
    if (route && streetNumber) {
      return `${route} ${streetNumber}`.trim();
    }
    if (route) return route;
    if (streetNumber) return streetNumber;
    if (fallbackAddress) {
      return extractFirstAddressPart(fallbackAddress);
    }
    return '';
  };

  const extractLocality = (addressComponents) => {
    return (
      extractAddressComponent(addressComponents, ['locality']) ||
      extractAddressComponent(addressComponents, ['sublocality', 'sublocality_level_1']) ||
      extractAddressComponent(addressComponents, ['administrative_area_level_2'])
    );
  };

  const extractPlaceData = (place) => {
    const addressComponents = place.address_components || [];
    
    const streetNumber = extractAddressComponent(addressComponents, ['street_number']);
    const route = extractAddressComponent(addressComponents, ['route']);
    const postalCode = extractAddressComponent(addressComponents, ['postal_code']);
    const jurisdiction = extractAddressComponent(addressComponents, ['administrative_area_level_1']);
    const locality = extractLocality(addressComponents);
    const address = buildStreetAddress(route, streetNumber, place.formatted_address);
    
    return {
      address,
      locality,
      jurisdiction,
      postalCode,
      location: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
    };
  };

  const onPlaceChanged = () => {
    if (!googlePlacesAutocompleteRef.current)
        return;

    const place = googlePlacesAutocompleteRef.current.getPlace();

    if (!place || !place.geometry)
        return;
    
    const placeData = extractPlaceData(place);
    
    if (onPlaceSelected) {
      onPlaceSelected(placeData);
    }
  };

  const handleInputChange = (event) => {
    if (formField) {
      formField.updateValue(event.target.value);
    }
  };

  const handleBlur = async (event) => {
    const newValue = event.target.value;
    if (formField && newValue !== formField.initialValue()) {
      formField.updateInitialValue(newValue);
      if (form) {
        await form.updateFieldWithValue(fieldName, newValue);
      } else if (field) {
        field.updateValue(newValue);
      }
    }
  };

  return (
    <FormFieldBase>
      {isGoogleMapsLoaded ? (
        <div style={{ width: '100%' }}>
          <Autocomplete
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: 'ar' },
              fields: ['address_components', 'geometry', 'formatted_address'],
              types: ['address'],
            }}
          >
            <TextField
              placeholder={placeholder || label}
              label={label}
              required={!formField?.isOptional}
              InputLabelProps={{ shrink: !!value }}
              disabled={disabled || readonly}
              error={formField?.hasValue && !formField?.isValid}
              style={style.fieldContainer}
              value={value}
              onChange={handleInputChange}
              onBlur={handleBlur}
              fullWidth
              sx={{
                '& .MuiInputLabel-root, & .MuiInputLabel-shrink, & .MuiOutlinedInput-root': {
                  fontSize: '.8rem',
                },
              }}
            />
          </Autocomplete>
        </div>
      ) : (
        <TextField
          placeholder={placeholder || label}
          label={label}
          required={!formField?.isOptional}
          InputLabelProps={{ shrink: !!value }}
          disabled={true}
          error={formField?.hasValue && !formField?.isValid}
          style={style.fieldContainer}
          value={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          fullWidth
          sx={{
            '& .MuiInputLabel-root, & .MuiInputLabel-shrink, & .MuiOutlinedInput-root': {
              fontSize: '.8rem',
            },
          }}
        />
      )}
    </FormFieldBase>
  );
}

const styles = (theme, width) => ({
  fieldContainer: {
    backgroundColor: theme.brand.field,
    justifyContent: 'center',
    borderRadius: '0.5rem',
    width: width,
  },
});

export default observer(FormGooglePlacesAutocompleteField);

